import React from "react";
import { useQueries } from "@tanstack/react-query";
import styled from "styled-components";
import { isPermitted } from "util/PermissionsMixin";
import { Spinner } from "components/common";
import { Row, Col } from "components/bootstrap";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import { IndexerClusterHealthSummary } from "components/indexers";
import ApiRoutes from "routing/ApiRoutes";
import { fetchPeriodically } from "logic/rest/FetchProvider";
import * as URLUtils from "util/URLUtils";
import useCurrentUser from "hooks/useCurrentUser";
import IndexerClusterHealthError from "./IndexerClusterHealthError";
const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;
const GET_INDEXER_CLUSTER_HEALTH = "indexerCluster.health";
const GET_INDEXER_CLUSTER_NAME = "indexerCluster.name";
const getIndexerClusterHealth = () => {
  const url = URLUtils.qualifyUrl(ApiRoutes.IndexerClusterApiController.health().url);
  return fetchPeriodically("GET", url);
};
const getIndexerClusterName = () => {
  const url = URLUtils.qualifyUrl(ApiRoutes.IndexerClusterApiController.info().url);
  return fetchPeriodically("GET", url);
};
const useLoadHealthAndName = (enabled) => {
  const options = { refetchInterval: 5e3, retry: 0, enabled };
  const [
    {
      data: healthData,
      isFetching: healthIsFetching,
      error: healthError,
      isSuccess: healthIsSuccess,
      isRefetching: healthIsRefetching
    },
    {
      data: nameData,
      isFetching: nameIsFetching,
      error: nameError,
      isSuccess: nameIsSuccess,
      isRefetching: nameIsRefetching
    }
  ] = useQueries({
    queries: [
      { queryKey: [GET_INDEXER_CLUSTER_HEALTH], queryFn: getIndexerClusterHealth, ...options },
      { queryKey: [GET_INDEXER_CLUSTER_NAME], queryFn: getIndexerClusterName, ...options }
    ]
  });
  return {
    health: healthData,
    name: nameData,
    error: healthError || nameError,
    loading: (healthIsFetching || nameIsFetching) && !healthIsRefetching && !nameIsRefetching,
    isSuccess: healthIsSuccess && nameIsSuccess
  };
};
const IndexerClusterHealth = ({ minimal = false }) => {
  const currentUser = useCurrentUser();
  const userHasRequiredPermissions = isPermitted(currentUser.permissions, "indexercluster:read");
  const { health, name, loading, error, isSuccess } = useLoadHealthAndName(userHasRequiredPermissions);
  if (!userHasRequiredPermissions) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, !minimal && /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("h2", null, (name == null ? void 0 : name.distribution) || "Elasticsearch", " cluster"), (name == null ? void 0 : name.distribution) === "OpenSearch" ? /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.OPEN_SEARCH_SETUP,
      text: "OpenSearch setup documentation",
      displayIcon: true
    }
  ) : /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.CONFIGURING_ES,
      text: "Elasticsearch setup documentation",
      displayIcon: true
    }
  )), isSuccess && /* @__PURE__ */ React.createElement(IndexerClusterHealthSummary, { health, name }), loading && /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Spinner, null)), error && /* @__PURE__ */ React.createElement(IndexerClusterHealthError, { error, name })));
};
export default IndexerClusterHealth;
